
import React, { useContext, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { UserProfile, Page, Puzzle, ImageEdits, defaultImageEdits, getSafeImageEdits } from '../types';
import { isUserOnline } from '../utils';
import { useAuth } from '../AuthContext';
import { usePuzzles } from '../PuzzleContext';
import { PageContext } from '../PageContext';
import PostCard from '../components/PostCard';
import { useAdmin } from '../AdminContext';
import ReportUserModal from '../components/ReportUserModal';
import ConfirmationModal from '../components/ConfirmationModal';
import BackButton from '../components/BackButton';
import BannerAd from '../components/BannerAd';

interface ProfilePageProps {
    userId?: string;
}

const quotes = [
    "Got a good one? Stump your friends!",
    "Create a masterpiece, perplex the masses.",
    "Share a mystery, challenge the world.",
    "What's in the picture? Start a new puzzle.",
    "Unleash your creativity, post a clue!"
];

const StatItem: React.FC<{ value: number | string; label: string }> = ({ value, label }) => (
    <div className="text-center">
        <p className="font-bold text-lg">{value}</p>
        <p className="text-xs">{label}</p>
    </div>
);

const ProfilePage: React.FC<ProfilePageProps> = ({ userId }) => {
    const { user: currentUser, userProfile: currentUserProfile, toggleFollow, allUsers, toggleBlockUser, usersLoading } = useAuth();
    const { puzzles, reportPuzzle, puzzlesLoading } = usePuzzles();
    const { reportUser, sendFriendRequest, sentRequests, banUser, deleteUser, sendDirectNotification } = useAdmin();
    const pageContext = useContext(PageContext);

    const [isReportModalOpen, setReportModalOpen] = useState(false);
    const [reportModalTriggerRect, setReportModalTriggerRect] = useState<DOMRect | null>(null);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [confirmModalProps, setConfirmModalProps] = useState({ title: '', message: '', onConfirm: () => {}, triggerRect: null as DOMRect | null, confirmText: 'Confirm' });
    
    const [quoteIndex, setQuoteIndex] = useState(0);
    const [quoteOpacity, setQuoteOpacity] = useState(1);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);

    const optionsMenuRef = useRef<HTMLDivElement>(null);
    const blockButtonRef = useRef<HTMLButtonElement>(null);
    const reportUserButtonRef = useRef<HTMLButtonElement>(null);
    const banButtonRef = useRef<HTMLButtonElement>(null);
    const deleteButtonRef = useRef<HTMLButtonElement>(null);
    
    useEffect(() => {
        const intervalId = setInterval(() => {
            setQuoteOpacity(0);
            setTimeout(() => {
                setQuoteIndex(prevIndex => (prevIndex + 1) % quotes.length);
                setQuoteOpacity(1);
            }, 500);
        }, 20000);
        return () => clearInterval(intervalId);
    }, []);

    const profile = useMemo(() => {
        return userId ? allUsers.find(u => u.uid === userId) : currentUserProfile;
    }, [userId, currentUserProfile, allUsers]);
    
    const userPuzzles = useMemo(() => {
        if (!profile || puzzlesLoading || usersLoading) return [];

        // Check if the profile being viewed has blocked the current user.
        if (currentUserProfile && profile.blockedUsers?.includes(currentUserProfile.uid)) {
            return []; // Return empty if the current user is blocked by the profile owner.
        }

        return puzzles.filter(p => p.creatorId === profile.uid)
                      .sort((a, b) => b.createdAt.toMillis() - a.createdAt.toMillis());
    }, [puzzles, profile, puzzlesLoading, usersLoading, currentUserProfile]);

    const feedWithAds = useMemo(() => {
        return userPuzzles;
    }, [userPuzzles]);
    
    // Unified loading state. Wait for everything before rendering.
    const isLoading = puzzlesLoading || usersLoading || !currentUserProfile || (userId && !profile);

    if (isLoading) { 
        return (
             <div className="flex items-center justify-center h-full">
                <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.3)', borderLeftColor: 'var(--primary-dark-purple)'}}></div>
            </div>
        );
    }
    
    if (!profile) {
        return <p className="text-center text-gray-500">User not found.</p>;
    }

    const isOwnProfile = profile.uid === currentUser?.uid;
    const isAdmin = currentUserProfile?.isAdmin;
    
    // Friend Logic
    // I follow them
    const isFollowing = currentUserProfile.following.includes(profile.uid);
    // They follow me
    const isFollower = profile.following.includes(currentUserProfile.uid);
    
    const isFriend = isFollowing && isFollower;
    const requestSent = sentRequests.has(profile.uid) || (isFollowing && !isFollower);

    const displayName = profile.displayName;
    const username = profile.username ? `@${profile.username}` : '';
    const photoURL = profile.photoURL;
    const bio = profile.bio;
    const backgroundUrl = profile.backgroundUrl;
    const photoEdits = getSafeImageEdits(profile.photoEdits);
    const backgroundEdits = getSafeImageEdits(profile.backgroundEdits);
    const score = profile.score;
    
    // Calculate Friends Count (Mutuals)
    const friendsCount = allUsers.filter(u => profile.following.includes(u.uid) && u.following.includes(profile.uid)).length;

    
    const openCreatePage = () => {
        pageContext?.switchPage(Page.CreatePost, 'Create Post');
    };

    const openEditPage = useCallback((puzzle: Puzzle) => {
        pageContext?.switchPage(Page.CreatePost, 'Edit Post', { puzzleToEdit: puzzle });
    }, [pageContext]);

    const handleCloseModal = () => {
        setReportModalOpen(false);
        setReportModalTriggerRect(null);
        setConfirmModalOpen(false);
    };

    const handleReportSubmit = (reason: string, details: string) => {
        reportUser(profile, reason, details);
        handleCloseModal();
        alert('Thank you for your report. Our moderation team will review it shortly.');
    };

    const handleMessage = () => {
        pageContext?.switchPage(Page.ChatConversation, profile.displayName, { recipient: profile });
    };

    const handleFriendAction = () => {
        if (isFriend) {
            if(window.confirm(`Unfriend ${displayName}?`)) {
                toggleFollow(profile.uid); // Unfollow them -> Breaks friendship
            }
        } else if (isFollowing) {
             if(window.confirm("Cancel friend request?")) {
                toggleFollow(profile.uid); // Unfollow -> Cancels request
            }
        } else {
            sendFriendRequest(profile.uid); // Will trigger toggleFollow(theirID) and send notification
        }
    };

    const handleAdvertiseClick = () => {
        if (!currentUserProfile) return;
        
        // Find all admins
        const admins = allUsers.filter(u => u.isAdmin);
        
        // Send notification to each admin
        admins.forEach(admin => {
            sendDirectNotification(admin.uid, {
                type: 'system' as any, // using system type or just a generic one
                text: `wants to upgrade to a Business Account!`,
                fromUser: {
                    id: currentUserProfile.uid,
                    name: currentUserProfile.displayName,
                    avatar: currentUserProfile.photoURL,
                }
            });
        });
        
        alert("Thanks for your interest! An admin has been notified and will contact you shortly to set up your Business Account.");
    };

    const handleEditClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isOwnProfile) {
            pageContext?.launchEditProfileModal(event.currentTarget.getBoundingClientRect());
        }
    };
    
    const handleBlockUserClick = () => {
        setShowOptionsMenu(false);
        setConfirmModalProps({
            title: `Block ${displayName}?`,
            message: 'They will not be able to find your profile, posts, or message you. They will not be notified that you have blocked them.',
            onConfirm: () => {
                toggleBlockUser(profile.uid);
                setConfirmModalOpen(false);
            },
            triggerRect: blockButtonRef.current?.getBoundingClientRect() || null,
            confirmText: 'Block',
        });
        setConfirmModalOpen(true);
    };

    const handleReportUserClick = (e: React.MouseEvent) => {
        setShowOptionsMenu(false);
        setReportModalTriggerRect(e.currentTarget.getBoundingClientRect());
        setReportModalOpen(true);
    };

    // Admin Actions
    const handleBanUserClick = () => {
        setShowOptionsMenu(false);
        setConfirmModalProps({
            title: `Ban ${displayName}?`,
            message: 'This user will be logged out and unable to access the app. This is an Admin action.',
            onConfirm: () => {
                banUser(profile.uid);
                setConfirmModalOpen(false);
            },
            triggerRect: banButtonRef.current?.getBoundingClientRect() || null,
            confirmText: 'Ban User',
        });
        setConfirmModalOpen(true);
    };

    const handleDeleteUserClick = () => {
        setShowOptionsMenu(false);
        setConfirmModalProps({
            title: `Delete ${displayName}?`,
            message: 'This will permanently delete this user profile. This cannot be undone.',
            onConfirm: () => {
                deleteUser(profile.uid);
                setConfirmModalOpen(false);
            },
            triggerRect: deleteButtonRef.current?.getBoundingClientRect() || null,
            confirmText: 'Delete User',
        });
        setConfirmModalOpen(true);
    };

    const backgroundStyle: React.CSSProperties = {
        filter: `brightness(${backgroundEdits.adjustments.brightness}%) contrast(${backgroundEdits.adjustments.contrast}%) saturate(${backgroundEdits.adjustments.saturate}%) blur(${backgroundEdits.adjustments.blur}px)`,
        transform: `scale(${backgroundEdits.adjustments.zoom}) translate(${backgroundEdits.adjustments.offsetX}px, ${backgroundEdits.adjustments.offsetY}px)`,
        backgroundImage: `url(${backgroundUrl})`,
    };

    const photoStyle: React.CSSProperties = {
        filter: `brightness(${photoEdits.adjustments.brightness}%) contrast(${photoEdits.adjustments.contrast}%) saturate(${photoEdits.adjustments.saturate}%) blur(${photoEdits.adjustments.blur}px)`,
        transform: `scale(${photoEdits.adjustments.zoom}) translate(${photoEdits.adjustments.offsetX}px, ${photoEdits.adjustments.offsetY}px)`,
    };
    
    // Privacy Logic: Show if Own Profile, Friend, or Public
    const canSeeContent = isOwnProfile || isFriend || !profile.isPrivate;

    return (
        <>
            <div className="space-y-4 -mt-4 -mx-4">
                {/* Header Section */}
                <div className={`relative rounded-b-3xl text-white p-4 overflow-hidden min-h-[400px] flex flex-col justify-start items-center ${!backgroundUrl && 'profile-header-bg'}`}>
                     {backgroundUrl ? (
                         <div className="absolute inset-0 bg-cover bg-center" style={backgroundStyle}></div>
                     ) : null}
                     <div className="absolute inset-0 bg-black/30"></div>
                      {!isOwnProfile && (
                        <div className="absolute top-4 right-4 z-30" ref={optionsMenuRef}>
                            <button onClick={() => setShowOptionsMenu(!showOptionsMenu)} className="text-white hover:text-gray-200">
                                <i className="ph-duotone ph-dots-three-outline-vertical text-2xl"></i>
                            </button>
                            {showOptionsMenu && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg text-gray-800 z-50">
                                    <button
                                        ref={blockButtonRef}
                                        onClick={handleBlockUserClick} 
                                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                                    >
                                        Block {displayName}
                                    </button>
                                    <button
                                        ref={reportUserButtonRef}
                                        onClick={handleReportUserClick} 
                                        className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-orange-600"
                                    >
                                        Report User
                                    </button>
                                    {isAdmin && (
                                        <>
                                            <div className="border-t my-1"></div>
                                            <button
                                                ref={banButtonRef}
                                                onClick={handleBanUserClick} 
                                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600 font-bold"
                                            >
                                                Admin: Ban User
                                            </button>
                                            <button
                                                ref={deleteButtonRef}
                                                onClick={handleDeleteUserClick} 
                                                className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 text-red-600 font-bold"
                                            >
                                                Admin: Delete User
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                     <div className="relative z-10 w-full pt-8">
                        <div className="flex flex-col items-center space-y-4">
                            <button
                                onClick={handleEditClick}
                                disabled={!isOwnProfile}
                                className={`relative w-20 h-20 flex-shrink-0 ${isOwnProfile ? 'cursor-pointer group' : ''}`}
                            >
                                <div className="relative w-full h-full rounded-full border-4 border-white/50 bg-gray-200 overflow-hidden profile-pic-glow">
                                    <div className={`frame-container ${photoEdits.frame} w-full h-full rounded-full`}>
                                        <img src={photoURL} alt={displayName} className={`w-full h-full object-cover ${photoEdits.filter}`} style={photoStyle}/>
                                    </div>
                                    {isOwnProfile && (
                                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                            <i className="ph-duotone ph-pencil-simple text-white text-2xl"></i>
                                        </div>
                                    )}
                                </div>
                                {isUserOnline(profile) && (
                                    <span className="absolute bottom-0 right-0 block h-5 w-5 rounded-full bg-green-500 border-2 border-white" title="Online"></span>
                                )}
                            </button>
                            <div className="text-center">
                                <h2
                                    className="text-2xl font.bold"
                                    style={{textShadow: '1px 1px 3px rgba(0,0,0,0.5)'}}
                                >
                                    {displayName}
                                    {profile.isBusiness && <span className="ml-2 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full font-extrabold align-middle inline-flex items-center gap-1"><i className="ph-fill ph-check-circle"></i> BUSINESS</span>}
                                    {profile.isAdmin && <span className="ml-2 text-xs bg-yellow-400 text-black px-2 py-0.5 rounded-full font-extrabold align-middle">ADMIN</span>}
                                    {profile.isBanned && <span className="ml-2 text-xs bg-red-600 text-white px-2 py-0.5 rounded-full font-extrabold align-middle">BANNED</span>}
                                </h2>
                                {username && (
                                    <p className="text-sm font-semibold text-gray-200 opacity-80" style={{textShadow: '1px 1px 3px rgba(0,0,0,0.5)'}}>{username}</p>
                                )}
                                <p className="text-sm mt-1" style={{textShadow: '1px 1px 3px rgba(0,0,0,0.5)'}}>{bio}</p>
                            </div>
                        </div>
                        <div className="mt-4 flex items-center space-x-2 w-full max-w-sm mx-auto">
                            {profile.isBusiness && profile.businessWebsite && (
                                <a 
                                    href={profile.businessWebsite} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md transition-all transform hover:scale-105 flex items-center justify-center gap-2"
                                >
                                    <i className="ph-fill ph-storefront text-lg"></i>
                                    {profile.businessActionText || 'Visit Store'}
                                </a>
                            )}
                            {!isOwnProfile && (
                                <div className="flex items-center space-x-2 flex-1">
                                    <button onClick={handleMessage} className="bg-white/20 backdrop-blur-sm p-2 rounded-lg shadow-md hover:bg-white/30"><i className="ph-duotone ph-paper-plane-tilt text-xl"></i></button>
                                    <button 
                                        onClick={handleFriendAction} 
                                        className={`flex-1 font-bold py-2 px-4 rounded-lg text-sm shadow-md transition-colors ${
                                            isFriend 
                                            ? 'bg-red-500 hover:bg-red-600 text-white' 
                                            : isFollowing 
                                                ? 'bg-gray-400 cursor-default text-white' 
                                                : 'bg-pink-500 hover:bg-pink-600 text-white'
                                        }`}
                                    >
                                        {isFriend ? 'Unfriend' : isFollowing ? 'Request Sent' : 'Add Friend'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="absolute bottom-4 z-20 w-full px-4 flex flex-col items-center space-y-4">
                        <div className="flex justify-center items-center space-x-8 w-full max-w-sm mx-auto">
                            <button
                                onClick={() => pageContext?.switchPage(Page.FriendsList, 'Friends', { initialTab: 'friends' })}
                                className="transition-transform hover:scale-110"
                            >
                                <StatItem value={friendsCount} label="Friends" />
                            </button>
                            <button
                                onClick={() => pageContext?.switchPage(Page.Leaderboard, 'Leaderboard')}
                                className="transition-transform hover:scale-110"
                            >
                                <StatItem value={score} label="Score" />
                            </button>
                        </div>
                    </div>
                </div>
                
                {isOwnProfile && (
                    <div className="px-4 space-y-3">
                         <div className="flex justify-center items-center flex-wrap gap-2 sm:gap-4">
                            <button
                                onClick={() => pageContext?.switchPage(Page.FriendsList, 'Friends', { initialTab: 'friends' })}
                                className="bg-[var(--primary-dark-purple)] text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md"
                            >
                                My Friends
                            </button>
                            
                            <button
                                onClick={handleEditClick}
                                className="bg-[var(--primary-dark-purple)] text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md"
                            >
                                Edit Profile
                            </button>

                            <button
                                onClick={() => pageContext?.switchPage(Page.ClueStore, 'Clue Store')}
                                className="bg-[var(--primary-dark-purple)] text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md"
                            >
                                Clue Store
                            </button>

                            <button
                                onClick={() => pageContext?.switchPage(Page.AIHome, 'AI Studio')}
                                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md flex items-center gap-1"
                            >
                                <i className="ph-fill ph-brain"></i>
                                AI Studio
                            </button>

                            <button
                                onClick={() => pageContext?.switchPage(Page.Leaderboard, 'Leaderboard')}
                                className="bg-[var(--primary-dark-purple)] text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md flex items-center gap-1"
                            >
                                <i className="ph-bold ph-trophy"></i>
                                Leaderboard
                            </button>

                            {!profile.isBusiness && !isAdmin && (
                                <button
                                    onClick={handleAdvertiseClick}
                                    className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-bold py-2 px-4 rounded-lg text-sm shadow-md flex items-center gap-1"
                                >
                                    <i className="ph-fill ph-storefront"></i>
                                    Advertise with us
                                </button>
                            )}
                        </div>

                        {isAdmin ? (
                             <button
                                onClick={() => pageContext?.switchPage(Page.Admin, 'Admin Panel')}
                                className="w-full bg-yellow-500 text-black font-extrabold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2 transform hover:scale-105 transition-transform"
                            >
                                <i className="ph-fill ph-shield-check text-xl"></i>
                                Admin Panel
                            </button>
                        ) : (
                            <div className="w-full flex justify-center mt-2">
                                <BannerAd />
                            </div>
                        )}
                    </div>
                )}

                {isOwnProfile && (
                     <div className="px-4">
                        <div
                            onClick={openCreatePage}
                            className="bg-[var(--primary-dark-purple)] p-3 rounded-lg shadow-md cursor-pointer hover:bg-purple-900 transition-colors"
                        >
                            <div className="flex items-center space-x-3">
                                <div className="flex-grow bg-white rounded-lg px-4 py-2 text-center flex items-center justify-center min-h-[40px]">
                                    <p
                                        className="text-3xl font.bold aurora-text"
                                        style={{
                                            fontFamily: "'Caveat', cursive",
                                            transition: 'opacity 0.5s ease-in-out',
                                            opacity: quoteOpacity,
                                        }}
                                    >
                                        {quotes[quoteIndex]}
                                    </p>
                                </div>
                                <i className="ph-duotone ph-image text-2xl text-[var(--accent-pink)]"></i>
                            </div>
                        </div>
                     </div>
                )}
                
                <div className="p-4">
                     {!canSeeContent ? (
                        <div className="text-center py-10">
                            <i className="ph-duotone ph-lock-simple text-5xl text-gray-400 mb-2"></i>
                            <h3 className="font-bold text-lg">This Account is Private</h3>
                            <p className="text-gray-500">Become friends with this account to see their posts.</p>
                        </div>
                     ) : (
                        <div className="space-y-4">
                            {feedWithAds.length > 0 ? (
                                feedWithAds.map(item => {
                                    return <PostCard key={item.id} puzzle={item as Puzzle} onReport={reportPuzzle} onEdit={openEditPage} />;
                                })
                            ) : (
                                <p className="text-center text-gray-400 pt-8">{displayName} hasn't posted anything yet.</p>
                            )}
                        </div>
                    )}
                </div>

            </div>
            <ConfirmationModal
                isOpen={isConfirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={confirmModalProps.onConfirm}
                title={confirmModalProps.title}
                message={confirmModalProps.message}
                triggerRect={confirmModalProps.triggerRect}
                confirmText={confirmModalProps.confirmText}
            />
            <ReportUserModal isOpen={isReportModalOpen} onClose={handleCloseModal} onSubmit={handleReportSubmit} userToReport={profile} triggerRect={reportModalTriggerRect}/>
        </>
    );
};

export default ProfilePage;
